/**
 * The access matrix for the ready-made job roles (2026-10-26).
 *
 * The owner's rule: a person sees the workspaces of their job and nothing else, not even read-only.
 * This file states that intent per role; it grants nothing. Access is still the permission set of the
 * role (`role-presets.ts`), enforced by the server inside every query (ADR-0006), and the menu, the
 * route guard and the dashboard derive what they show from those same permissions.
 *
 * The tests hold the three together: each preset's permissions must produce exactly the workspaces
 * and dashboard sections listed here (web `access-matrix.test.ts`), must hold no permission in its
 * forbidden areas (`role-presets.test.ts`), and the server must refuse a forbidden workspace's data
 * to a holder of that role alone (API `access-matrix.int-test.ts`). The generated page-level matrix is
 * `docs/architecture/access-matrix.md`.
 *
 * A **workspace** is one navigation group. `overview` is everyone's personal space: dashboard, own
 * tasks and calendar, own payslips and requests, and the approval inbox only for people who decide.
 */
export const WORKSPACES = [
  'overview',
  'sales',
  'inventory',
  'collections',
  'treasury',
  'accounting',
  'procurement',
  'stores',
  'construction',
  'handover',
  'hr',
  'marketing',
  'admin',
  'settings',
] as const;
export type Workspace = (typeof WORKSPACES)[number];

/** The landing dashboard's sections; each requests its data only when shown. */
export const DASHBOARD_SECTIONS = [
  'executive',
  'period',
  'attention',
  'collections',
  'sales',
  'inventory',
  'marketing',
  'team',
  'salesAdmin',
] as const;
export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

export interface RoleAccess {
  workspaces: readonly Workspace[];
  dashboard: readonly DashboardSection[];
  /**
   * Permission areas the role must never hold — a prefix such as `collection.` or one exact key.
   * Pinned so that a later preset change cannot quietly reopen a section the owner closed.
   */
  forbidden: readonly string[];
}

const NOT_SALES = [
  'collection.',
  'finance.',
  'treasury.',
  'marketing.',
  'hr.',
  'org.',
  'security.',
  'procurement.',
  'stores.',
  'construction.',
  'audit.',
  'settings.',
  'company.',
  'handover.',
  'aftersales.',
  'portal.',
] as const;

const NOT_MARKETING = [
  'sales.',
  'collection.',
  'finance.',
  'treasury.',
  'hr.',
  'org.',
  'security.',
  'procurement.',
  'stores.',
  'construction.',
  'crm.customer.',
  'crm.opportunity.',
  'inventory.unit.',
] as const;

/** No money moves and no decision is taken by configuration (BD-59, SEC-031). */
const NOT_MONEY_OR_DECISIONS = [
  'approval.request.approve',
  'approval.request.reject',
  'treasury.payment.',
  'treasury.movement.record',
  'treasury.transfer.create',
  'collection.refund.pay',
  'finance.journal.post',
] as const;

/** Everything but the Sales Admin's own operational dashboard, which replaces the others for that job. */
const ALL_REPORT_SECTIONS: readonly DashboardSection[] = DASHBOARD_SECTIONS.filter(
  (section) => section !== 'salesAdmin',
);

const COMPANY_WIDE: readonly Workspace[] = [
  'overview',
  'sales',
  'inventory',
  'collections',
  'treasury',
  'accounting',
  'procurement',
  'stores',
  'construction',
  'handover',
  'hr',
  'marketing',
  'admin',
];

export const ROLE_ACCESS: Readonly<Record<string, RoleAccess>> = {
  'system-administrator': {
    workspaces: ['overview', 'inventory', 'treasury', 'accounting', 'admin', 'settings'],
    dashboard: ['executive', 'attention', 'inventory'],
    forbidden: [...NOT_MONEY_OR_DECISIONS],
  },
  'senior-management': {
    workspaces: COMPANY_WIDE,
    dashboard: ALL_REPORT_SECTIONS,
    forbidden: ['security.', 'settings.manage', 'treasury.payment.execute', 'treasury.movement.'],
  },
  'executive-manager': {
    workspaces: COMPANY_WIDE,
    dashboard: ALL_REPORT_SECTIONS,
    forbidden: ['security.', 'settings.manage', 'treasury.payment.execute', 'treasury.movement.'],
  },
  'sales-manager': {
    workspaces: ['overview', 'sales', 'inventory'],
    dashboard: ['executive', 'period', 'attention', 'sales', 'inventory', 'team'],
    forbidden: NOT_SALES,
  },
  'sales-representative': {
    workspaces: ['overview', 'sales', 'inventory'],
    dashboard: ['executive', 'period', 'attention', 'sales', 'inventory'],
    forbidden: [...NOT_SALES, 'crm.lead.assign', 'crm.distribution.', 'sales.reservation.confirm'],
  },
  'sales-admin': {
    workspaces: ['overview', 'sales', 'inventory'],
    dashboard: ['salesAdmin'],
    forbidden: [
      ...NOT_SALES,
      'sales.commission.',
      'sales.contract.activate',
      'sales.contract.cancel',
      'sales.contract.amend',
      'sales.contract.sign',
      'sales.contract.assign',
      'sales.contract.replaceUnit',
      'sales.contract.releaseUnit',
      'sales.reservation.cancel',
      'sales.reservation.extend',
      'approval.',
      'inventory.unit.manage',
      'inventory.project.manage',
      'inventory.price.',
      'inventory.plan.manage',
      'inventory.record.',
      'crm.record.',
      'crm.customer.merge',
      'crm.customer.transfer',
      'crm.customer.viewIdentity',
      'referenceData.manage',
    ],
  },
  'marketing-manager': {
    workspaces: ['overview', 'sales', 'marketing'],
    dashboard: ['executive', 'attention', 'sales', 'marketing'],
    forbidden: NOT_MARKETING,
  },
  'marketing-employee': {
    workspaces: ['overview', 'sales', 'marketing'],
    dashboard: ['executive', 'attention', 'sales', 'marketing'],
    forbidden: [...NOT_MARKETING, 'marketing.campaign.manage'],
  },
  'finance-manager': {
    workspaces: ['overview', 'sales', 'collections', 'treasury', 'accounting', 'procurement', 'hr'],
    dashboard: ['executive', 'period', 'attention', 'collections'],
    forbidden: [
      'crm.lead.',
      'crm.opportunity.',
      'marketing.',
      'security.',
      'construction.',
      'handover.',
      'aftersales.',
      'treasury.payment.execute',
      'treasury.movement.record',
    ],
  },
  accountant: {
    workspaces: ['overview', 'sales', 'collections', 'treasury', 'accounting', 'procurement'],
    dashboard: ['executive', 'period', 'attention', 'collections'],
    forbidden: [
      'crm.lead.',
      'marketing.',
      'hr.',
      'security.',
      'construction.',
      'stores.',
      'inventory.unit.',
      'approval.request.approve',
      'treasury.payment.',
    ],
  },
  'collections-officer': {
    workspaces: ['overview', 'sales', 'collections', 'treasury'],
    dashboard: ['executive', 'period', 'attention', 'collections'],
    forbidden: [
      'finance.',
      'marketing.',
      'hr.',
      'security.',
      'org.',
      'procurement.',
      'stores.',
      'construction.',
      'crm.lead.',
      'treasury.payment.execute',
      'approval.request.approve',
    ],
  },
  cashier: {
    workspaces: ['overview', 'collections', 'treasury'],
    dashboard: ['attention'],
    forbidden: [
      'crm.',
      'sales.',
      'finance.',
      'marketing.',
      'hr.',
      'security.',
      'procurement.',
      'treasury.payment.request',
      'approval.request.approve',
    ],
  },
  'procurement-manager': {
    workspaces: ['overview', 'procurement', 'stores'],
    dashboard: ['attention'],
    forbidden: ['crm.', 'sales.', 'collection.', 'marketing.', 'hr.', 'security.', 'treasury.'],
  },
  'procurement-officer': {
    workspaces: ['overview', 'treasury', 'procurement'],
    dashboard: ['attention'],
    forbidden: [
      'crm.',
      'sales.',
      'collection.',
      'marketing.',
      'hr.',
      'security.',
      'treasury.payment.execute',
      'approval.request.approve',
    ],
  },
  storekeeper: {
    workspaces: ['overview', 'procurement', 'stores'],
    dashboard: ['attention'],
    forbidden: [
      'crm.',
      'sales.',
      'collection.',
      'finance.',
      'treasury.',
      'marketing.',
      'hr.',
      'security.',
    ],
  },
  'project-manager': {
    workspaces: ['overview', 'inventory', 'procurement', 'stores', 'construction', 'handover'],
    dashboard: ['executive', 'attention', 'inventory'],
    forbidden: ['crm.', 'sales.', 'collection.', 'treasury.', 'marketing.', 'hr.', 'security.'],
  },
  'site-engineer': {
    workspaces: ['overview', 'inventory', 'procurement', 'stores', 'construction', 'handover'],
    dashboard: ['executive', 'attention', 'inventory'],
    forbidden: [
      'crm.',
      'sales.',
      'collection.',
      'treasury.',
      'finance.',
      'marketing.',
      'hr.',
      'security.',
    ],
  },
  'contractor-certificate-officer': {
    workspaces: ['overview', 'treasury', 'procurement', 'construction'],
    dashboard: ['attention'],
    forbidden: [
      'crm.',
      'sales.',
      'collection.',
      'finance.',
      'marketing.',
      'hr.',
      'security.',
      'treasury.payment.execute',
    ],
  },
  'hr-officer': {
    workspaces: ['overview', 'hr', 'admin'],
    dashboard: ['attention'],
    forbidden: [
      'crm.',
      'sales.',
      'collection.',
      'finance.',
      'treasury.',
      'marketing.',
      'procurement.',
      'security.',
      'hr.payroll.',
    ],
  },
  'payroll-officer': {
    workspaces: ['overview', 'sales', 'treasury', 'hr'],
    dashboard: ['attention'],
    forbidden: [
      'crm.',
      'collection.',
      'marketing.',
      'procurement.',
      'security.',
      'finance.',
      'hr.payroll.approve',
      'hr.payroll.post',
      'treasury.payment.execute',
    ],
  },
  'handover-officer': {
    workspaces: ['overview', 'sales', 'inventory', 'handover'],
    dashboard: ['executive', 'attention', 'inventory'],
    forbidden: [
      'collection.',
      'finance.',
      'treasury.',
      'marketing.',
      'hr.',
      'security.',
      'procurement.',
    ],
  },
  'customer-service': {
    workspaces: ['overview', 'sales', 'inventory', 'handover'],
    dashboard: ['executive', 'attention', 'inventory'],
    forbidden: [
      'collection.',
      'finance.',
      'treasury.',
      'marketing.',
      'hr.',
      'security.',
      'procurement.',
    ],
  },
};

/** Whether a permission falls in one of a role's forbidden areas. */
export function isForbiddenFor(access: RoleAccess, permission: string): boolean {
  return access.forbidden.some((area) =>
    area.endsWith('.') ? permission.startsWith(area) : permission === area,
  );
}
